import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { getFunctions, httpsCallable } from "firebase/functions";
import { Link } from 'react-router-dom';
import './PayoutsPage.css'; // Import the CSS file

const PayoutsPage = () => {
  const { jeweller } = useAuth();
  const [dashboardUrl, setDashboardUrl] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const createDashboardSession = async () => {
      if (!jeweller?.id) {
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        const functions = getFunctions();
        const createStripeConnectDashboard = httpsCallable(functions, 'createStripeConnectDashboard');
        
        const result = await createStripeConnectDashboard({
          returnUrl: window.location.origin + '/payouts'
        });
        
        console.log('Dashboard result:', result.data);
        setDashboardUrl(result.data.url);
        setError(null);
      } catch (error) {
        console.error('Error creating dashboard session:', error);
        setError(`Failed to load payout dashboard: ${error.message}`);
      } finally {
        setIsLoading(false);
      }
    };

    createDashboardSession();
  }, [jeweller]);

  if (!jeweller) {
    return (
      <div className="payouts-page">
        <div className="content-container">
          <div className="message-card">
            <h1 className="message-title">Access Denied</h1>
            <p className="message-text">Please log in to view your payout information.</p>
          </div>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="payouts-page">
        <div className="content-container">
          <div className="loading-card">
            <div className="spinner spinner--large"></div>
            <p className="loading-text">Setting up your payout dashboard...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="payouts-page">
        <div className="content-container">
          <div className="error-card">
            <h1 className="error-title">Error</h1>
            <p className="error-text">{error}</p>
            <button 
              onClick={() => window.location.reload()}
              className="btn btn--error"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!dashboardUrl) {
    return (
      <div className="payouts-page">
        <div className="content-container">
          <div className="warning-card">
            <h1 className="warning-title">Setup Required</h1>
            <p className="warning-text">
              Your Stripe account needs to be set up before you can view payouts.
            </p>
            <Link 
              to="/dashboard" 
              className="btn btn--warning"
            >
              Complete Setup
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="payouts-page">
      {/* Header */}
      <div className="page-header-bar">
        <div className="header-container">
          <div className="header-content">
            <div className="header-nav">
              <Link 
                to="/dashboard" 
                className="back-link"
              >
                ← Back to Dashboard
              </Link>
              <h1 className="page-title">Payouts</h1>
            </div>
            <div className="company-name">
              {jeweller.companyName}
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="content-container">
        
        {/* Payout Dashboard Card */}
        <div className="dashboard-card">
          <div className="dashboard-content">
            <div className="icon-container icon-container--primary">
              <svg className="icon-large" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" 
                  d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
              </svg>
            </div>
            <h2 className="dashboard-title">Manage Your Payouts</h2>
            <p className="dashboard-description">
              Access your complete Stripe dashboard to view payout history, update banking details, and manage your account settings.
            </p>
          </div>
          
          <a
            href={dashboardUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="btn btn--primary btn--large btn--with-icon"
          >
            Open Payout Dashboard
            <svg className="btn-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" 
                d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
            </svg>
          </a>
          
          <p className="helper-text">
            Opens in a new tab - managed securely by Stripe
          </p>
        </div>

        {/* Features Grid */}
        <div className="features-grid">
          <div className="feature-card">
            <div className="feature-header">
              <div className="icon-container icon-container--success">
                <svg className="icon-small" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" 
                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="feature-title">Track Payments</h3>
            </div>
            <ul className="feature-list">
              <li>View all payout history</li>
              <li>Check pending transfers</li>
              <li>Download detailed statements</li>
              <li>Monitor daily/weekly totals</li>
            </ul>
          </div>
          
          <div className="feature-card">
            <div className="feature-header">
              <div className="icon-container icon-container--info">
                <svg className="icon-small" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" 
                    d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" 
                    d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </div>
              <h3 className="feature-title">Manage Settings</h3>
            </div>
            <ul className="feature-list">
              <li>Update bank account details</li>
              <li>Change payout schedule</li>
              <li>Set notification preferences</li>
              <li>Manage tax documents</li>
            </ul>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="quick-actions-section">
          <h3 className="section-title">Quick Actions</h3>
          <div className="quick-actions-grid">
            <Link 
              to="/invoices" 
              className="action-card"
            >
              <h4 className="action-title">View Invoices</h4>
              <p className="action-description">Check which invoices have been paid</p>
            </Link>
            <Link 
              to="/invoices/new" 
              className="action-card"
            >
              <h4 className="action-title">Create Invoice</h4>
              <p className="action-description">Send a new invoice to a customer</p>
            </Link>
            <Link 
              to="/projects" 
              className="action-card"
            >
              <h4 className="action-title">Manage Projects</h4>
              <p className="action-description">View and organize your jewelry projects</p>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PayoutsPage;